import DesignSystemAppProvider from '@atlaskit/app-provider';
import { FlagsProvider } from '@atlaskit/flag';
import UFOSegment from '@atlaskit/react-ufo/segment';
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import {
	Main,
	Root,
	SideNav,
	SideNavToggleButton,
	TopBar,
	UserActions,
} from '@atlassian/navigation-system';
import { HomeActions, NavLogo } from '@atlassian/navigation-system/top-nav';
import { ErrorBoundary } from '@sentry/react';
import type { ReactNode } from 'react';
import { Suspense, useEffect } from 'react';
import { useQueryLoader } from 'react-relay';
import { createBrowserHistory, RouteComponent, Router, useRouter } from 'react-resource-router';
import { createResourcesPlugin } from 'react-resource-router/resources';

import { CustomRouterLink } from '../common/components/custom-router-link';
import { LoadingSkeleton } from '../common/components/skeleton/skeleton';
import type { userPermContextProviderQuery } from '../controllers/__generated__/userPermContextProviderQuery.graphql';
import Query from '../controllers/__generated__/userPermContextProviderQuery.graphql';
import { FeatureGateClient } from '../controllers/feature-gates';
import { UserPermContextProvider } from '../controllers/user-perm-context-provider';
import { createAppRoutes } from '../routes/index';
import { useAnalyticsWebClient } from '../utils/analytics/context';
import { useAuthData } from '../utils/auth-context';
import { getThemePreference } from '../utils/color-theme';
import { useTenantData } from '../utils/tenant-context';
import { useUserPermData } from '../utils/user-perm-context';

import { RovoDevAgentsIcon, RovoDevAgentsLogo } from './home/common/icons';
import { UserProfile } from './profile';

const history = createBrowserHistory();
const resourcesPlugin = createResourcesPlugin({
	resourceData: undefined,
});

/*
 * Components for composing top and side navigation
 */

const AtlassianProductHome = () => {
	const [routeState] = useRouter();

	const Logo = routeState.route?.logo;

	if (Logo) {
		return <Logo />;
	}

	return (
		<NavLogo href="/" logo={RovoDevAgentsLogo} icon={RovoDevAgentsIcon} label="Rovo Dev Agents" />
	);
};

const AppRouteComponent = () => {
	const [routerState] = useRouter();

	const SidebarComponent = routerState.route?.navigation;
	const { user } = useAuthData();
	const ufoName = routerState.route?.analyticsName;

	return (
		<Root>
			<TopBar>
				<HomeActions>
					{SidebarComponent && (
						<SideNavToggleButton
							defaultCollapsed
							collapseLabel="Collapse sidebar"
							expandLabel="Expand sidebar"
						/>
					)}
					{/* <AppSwitcher label="App switcher" /> */}
					<AtlassianProductHome />
				</HomeActions>
				{user && (
					<UserActions>
						<UserProfile user={user} />
					</UserActions>
				)}
			</TopBar>
			{SidebarComponent && (
				<SideNav defaultWidth={200}>
					<SidebarComponent />
				</SideNav>
			)}
			<Main isFixed>
				<UFOSegment name={ufoName ?? ''}>
					<RouteComponent />
				</UFOSegment>
			</Main>
		</Root>
	);
};

const BlockingAtlassianProviders = ({ children }: { children: ReactNode }) => {
	return (
		<DesignSystemAppProvider
			routerLinkComponent={CustomRouterLink}
			defaultColorMode={getThemePreference().colorMode}
		>
			<FeatureGateClient>{children}</FeatureGateClient>
		</DesignSystemAppProvider>
	);
};

const LazyAppRouterWithUsersPermsContext = ({ children }: { children: ReactNode }) => {
	const [userPermsQueryRef, loadUserPermsQuery] =
		useQueryLoader<userPermContextProviderQuery>(Query);
	const { tenantContexts } = useTenantData();
	const { user } = useAuthData();
	const cloudID = tenantContexts[0]?.cloudId || '';
	const atlassianAccountId = user.accountId;

	useEffect(() => {
		loadUserPermsQuery({
			cloudID,
			atlassianAccountId,
		});
	}, [loadUserPermsQuery, cloudID, atlassianAccountId]);

	return (
		userPermsQueryRef && (
			<UserPermContextProvider queryRef={userPermsQueryRef}>
				<LazyAppRouter>{children}</LazyAppRouter>
			</UserPermContextProvider>
		)
	);
};

const LazyAppRouter = ({ children }: { children: ReactNode }) => {
	const { rovoDevAgentsUser } = useUserPermData();
	const { tenantContexts } = useTenantData();
	const devaiEntitlementId = tenantContexts[0]?.devaiEntitlementId;
	return (
		<Router
			routes={createAppRoutes(rovoDevAgentsUser.isAdmin, devaiEntitlementId !== null)}
			history={history}
			plugins={[resourcesPlugin]}
			basePath="/dev-agents"
		>
			{children}
		</Router>
	);
};

const AppWithRoute = ({ children }: { children: ReactNode }) => {
	const [{ route }] = useRouter();
	const analyticsWebClient = useAnalyticsWebClient();

	useEffect(() => {
		if (route?.analyticsName) {
			/*
			 * Start instrumenting the page load event.
			 * We invoke `traceUFOPageLoad` once more
			 * when we have the route context.
			 * It attaches the route name to the event.
			 * */
			traceUFOPageLoad(route.analyticsName);

			analyticsWebClient.sendOperationalEvent({
				action: 'rendered',
				actionSubject: `app.${route.analyticsName}`,
				source: 'app',
			});
		}
	}, [route?.analyticsName]);
	return (
		<ErrorBoundary
			onError={(err) => {
				analyticsWebClient.sendOperationalEvent({
					action: 'error',
					actionSubject: `app.${route.analyticsName}`,
					source: 'app',
					attributes: {
						message: err?.message,
					},
				});
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export const App = () => {
	const analyticsWebClient = useAnalyticsWebClient();

	return (
		<ErrorBoundary
			onError={(err) => {
				analyticsWebClient.sendOperationalEvent({
					action: 'error',
					actionSubject: 'appComponent',
					source: 'app',
					attributes: {
						message: err.message,
					},
				});
			}}
		>
			<Suspense fallback={<LoadingSkeleton />}>
				<BlockingAtlassianProviders>
					<LazyAppRouterWithUsersPermsContext>
						<AppWithRoute>
							<FlagsProvider>
								<AppRouteComponent />
							</FlagsProvider>
						</AppWithRoute>
					</LazyAppRouterWithUsersPermsContext>
				</BlockingAtlassianProviders>
			</Suspense>
		</ErrorBoundary>
	);
};
